import En from '@/common/classes/En'
import { normalizeConditionResult } from '@/common/utils/evaluateLayoutCondition'
import { resolveLayoutScopedClientFields } from '@/common/utils/layoutScopedClientFields'
import { startWorkflowMeasure } from '@/common/utils/performanceTrace'

type EnInit = ConstructorParameters<typeof En>[0]

export function wrapTableColumnConditionScript(condition: string): string {
  const trimmed = String(condition ?? '').trim()
  if (!trimmed) {
    return 'true'
  }
  const expr = trimmed.replace(/;\s*$/, '')

  return `return (${expr});`
}

export type EvaluateTableColumnConditionParams = {
  condition: string
  pageData: unknown
  formProvider?: {
    getForm: (opts: { formId: string | number; panelId?: string | number }) => Record<string, unknown>
    getElements?: EnInit['getElements']
    getActions?: () => EnInit['actions']
  } | null
  formId?: string | number
  panelId?: string | number
  elementId?: string | number
  measureLabel?: string
}

/**
 * Вычисляет `<condition>` столбца Table/EdiTable.
 * `data` — layout-scoped currentData (uniData + jsResult.changed_data), как в action pipeline.
 */
export async function evaluateTableColumnCondition(
  params: EvaluateTableColumnConditionParams,
): Promise<boolean> {
  const rawCondition = String(params.condition ?? '').trim()
  if (!rawCondition) {
    return true
  }

  const endMeasure = startWorkflowMeasure(params.measureLabel ?? 'table.column.condition.evaluate', {
    formId: params.formId,
    panelId: params.panelId,
    elementId: params.elementId,
  })

  try {
    const pageData = params.pageData as Record<string, unknown>
    const formRoot = pageData?.form
    const currentDataFields = resolveLayoutScopedClientFields(pageData, formRoot)

    const formProvider = params.formProvider
    const en = new En({
      controls: formProvider ?? { getForm: () => currentDataFields },
      elements: {},
      getElements: formProvider?.getElements,
      actions: formProvider?.getActions ? formProvider.getActions() : {},
      pageData,
      formId: params.formId != null ? Number(params.formId) : undefined,
      panelId: params.panelId != null ? Number(params.panelId) : undefined,
      elementId: params.elementId != null ? Number(params.elementId) : undefined,
    })

    en._resetData(currentDataFields)

    const result = await en.run(wrapTableColumnConditionScript(rawCondition))
    return normalizeConditionResult(result)
  } catch (error) {
    console.error('Error evaluating table column condition:', error)
    return false
  } finally {
    endMeasure()
  }
}
